
import React, { useMemo, useState } from "react";

/**
 * Faqs Component
 * props:
 *  - data: { faqs: [{ id, q, a, tags:[] }], meta?: {...} }
 */
export default function FaqPage({ data }) {
  const faqs = Array.isArray(data?.faqs) ? data.faqs : [];
console.log(data)
  const [query, setQuery] = useState("");
  const [activeTags, setActiveTags] = useState(new Set());

  // Build tag cloud with counts
  const tagStats = useMemo(() => {
    const map = new Map();
    faqs.forEach((f) =>
      (f.tags || []).forEach((t) => map.set(t, (map.get(t) || 0) + 1))
    );
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [faqs]);

  const toggleTag = (tag) => {
    const next = new Set(activeTags);
    next.has(tag) ? next.delete(tag) : next.add(tag);
    setActiveTags(next);
  };

  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  // Filtered list
  const filtered = useMemo(() => {
    const q = norm(query).trim();
    const terms = q.length ? q.split(/\s+/) : [];
    return faqs.filter((f) => {
      // tag filter (all selected tags must be present)
      if (activeTags.size) {
        const tags = new Set(f.tags || []);
        for (const t of activeTags) if (!tags.has(t)) return false;
      }
      // text filter
      if (!terms.length) return true;
      const hay = `${norm(f.q)} ${norm(f.a)} ${(f.tags || [])
        .map(norm)
        .join(" ")}`;
      return terms.every((t) => hay.includes(t));
    });
  }, [faqs, query, activeTags]);

  return (
    <div
      style={{
        fontFamily: "Inter, system-ui, sans-serif",
        color: "#222",
        maxWidth: 900,
        margin: "0 auto",
        padding: "24px 16px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <h1 style={{ fontSize: 24, margin: 0 }}>FAQs</h1>
        {data?.meta?.updatedAt && (
          <div style={{ fontSize: 12, color: "#666" }}>
            Updated: {data.meta.updatedAt}
          </div>
        )}
      </div>

      {/* Search */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          alignItems: "center",
        }}
      >
        <input
          aria-label="Search FAQs"
          placeholder="Search by question, answer, or tag…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            flex: 1,
            padding: "12px 14px",
            borderRadius: 10,
            border: "1px solid #e3e3e3",
            outline: "none",
          }}
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #e3e3e3",
              background: "#fff",
              cursor: "pointer",
            }}
            aria-label="Clear search"
          >
            Clear
          </button>
        )}
      </div>

      {/* Tag filters */}
      {!!tagStats.length && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 18,
          }}
        >
          {tagStats.map(([tag, count]) => {
            const active = activeTags.has(tag);
            return (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                aria-pressed={active}
                style={{
                  padding: "8px 12px",
                  borderRadius: 999,
                  border: active ? "1px solid #00a082" : "1px solid #e3e3e3",
                  background: active ? "#e8fbf6" : "#fff",
                  color: active ? "#007e67" : "#333",
                  cursor: "pointer",
                  fontSize: 13,
                }}
                title={`${count} item(s)`}
              >
                #{tag} <span style={{ opacity: 0.6 }}>({count})</span>
              </button>
            );
          })}
          {activeTags.size > 0 && (
            <button
              onClick={() => setActiveTags(new Set())}
              style={{
                padding: "8px 12px",
                borderRadius: 999,
                border: "1px solid #e3e3e3",
                background: "#fff",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Clear tags
            </button>
          )}
        </div>
      )}

      {/* Results info */}
      <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
        Showing <strong>{filtered.length}</strong> of{" "}
        <strong>{faqs.length}</strong> FAQs
        {activeTags.size > 0 && (
          <>
            {" "}
            • Tags:{" "}
            {[...activeTags]
              .map((t) => `#${t}`)
              .join(", ")}
          </>
        )}
        {query && ` • Query: “${query}”`}
      </div>

      {/* FAQ List (collapsible via <details>) */}
      <div style={{ display: "grid", gap: 10 }}>
        {filtered.length === 0 ? (
          <div
            style={{
              border: "1px dashed #ddd",
              padding: 18,
              borderRadius: 12,
              color: "#666",
            }}
          >
            No results. Try different keywords or clear filters.
          </div>
        ) : (
          filtered.map((f) => (
            <details
              key={f.id}
              style={{
                background: "#fff",
                border: "1px solid #eee",
                borderRadius: 12,
                padding: "10px 14px",
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 600,
                  listStyle: "none",
                  outline: "none",
                }}
              >
                {f.q}
              </summary>
              <div style={{ marginTop: 8, color: "#444", lineHeight: 1.6 }}>
                <p style={{ marginTop: 6, marginBottom: 10, whiteSpace: "pre-wrap" }}>
                  {f.a}
                </p>
                {!!(f.tags || []).length && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(f.tags || []).map((t) => (
                      <span
                        key={t}
                        onClick={() => toggleTag(t)}
                        role="button"
                        tabIndex={0}
                        title="Filter by tag"
                        style={{
                          fontSize: 12,
                          background: "#f5f7f9",
                          border: "1px solid #e6e9ef",
                          padding: "4px 8px",
                          borderRadius: 999,
                          cursor: "pointer",
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") toggleTag(t);
                        }}
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </details>
          ))
        )}
      </div>
    </div>
  );
}
